import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import AdminShell from "../../components/AdminShell";
import QuestionPicker from "../../components/QuestionPicker";
import AttemptReview from "../../components/AttemptReview";
import { toast } from "sonner";
import {
  Loader2, X, QrCode, ExternalLink, Users, CheckCircle2, Camera,
  ClipboardCheck, Lock, Unlock, ClipboardList, Eye,
} from "lucide-react";

/**
 * Assignments: worksheets that have actually been given out.
 *
 * Each one carries its own copy of the question list, taken when it was
 * issued. That is why a worksheet stays editable: changing the master cannot
 * reach back into work a child has started.
 *
 * An issue can still be corrected while nobody has touched it - catching a
 * wrong question five minutes after sending is normal. The moment anyone types
 * an answer, uploads working or hands in, the question list freezes.
 */

function Detail({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [lock, setLock] = useState(null);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(false);
  const [codes, setCodes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [reviewing, setReviewing] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/assignments/${id}`)
      .then((r) => { setData(r.data); setCodes(r.data.question_codes || []); })
      .catch((e) => setErr(e.response?.data?.detail || "Could not load it"));
    api.get(`/admin/issues/${id}/lock`).then((r) => setLock(r.data)).catch(() => setLock(null));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const toggle = (c) => setCodes((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c]));

  const saveQuestions = () => {
    setSaving(true);
    api.patch(`/admin/issues/${id}`, { question_codes: codes })
      .then(() => { toast.success("Questions updated"); setEditing(false); load(); onChanged(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Could not change them"))
      .finally(() => setSaving(false));
  };

  const uploadUrl = (token) => `${window.location.origin}/upload/${token}`;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-start justify-center overflow-y-auto p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl my-6 shadow-xl">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <h2 className="font-semibold text-slate-800">{data?.title || "Assignment"}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="p-5">
          {err && <p className="text-sm text-red-700">{err}</p>}
          {!data && !err && (
            <p className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 size={15} className="animate-spin" /> Loading…
            </p>
          )}
          {data && (
            <>
              {lock && (
                <div className={`mb-4 flex items-start gap-2.5 rounded-lg border px-3.5 py-2.5 ${
                  lock.locked ? "border-amber-200 bg-amber-50" : "border-green-200 bg-green-50"}`}>
                  {lock.locked
                    ? <Lock size={15} className="mt-0.5 shrink-0 text-amber-600" />
                    : <Unlock size={15} className="mt-0.5 shrink-0 text-green-600" />}
                  <p className={`text-sm ${lock.locked ? "text-amber-900" : "text-green-900"}`}>
                    {lock.locked
                      ? <>The questions are locked — {lock.why}. Edit the worksheet and give it out again instead.</>
                      : <>Nobody has started yet, so you can still change the questions.</>}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400">
                  Questions ({data.question_codes.length})
                </h3>
                {!lock?.locked && !editing && (
                  <button onClick={() => setEditing(true)}
                    className="text-xs border border-slate-300 rounded-lg px-2.5 py-1 hover:bg-slate-50">
                    Change them
                  </button>
                )}
              </div>

              {!editing ? (
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {data.question_codes.map((c) => (
                    <span key={c} className="font-mono text-xs border border-slate-200 bg-slate-50 rounded px-2 py-1">{c}</span>
                  ))}
                </div>
              ) : (
                <div className="mb-5">
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {codes.map((c) => (
                      <button key={c} onClick={() => toggle(c)}
                        className="font-mono text-xs bg-slate-800 text-white rounded-full pl-2.5 pr-1.5 py-1 flex items-center gap-1">
                        {c} <X size={11} />
                      </button>
                    ))}
                  </div>
                  <QuestionPicker chosen={codes} onToggle={toggle} />
                  <div className="flex gap-2 mt-3">
                    <button onClick={saveQuestions} disabled={saving || codes.length === 0}
                      className="text-sm font-semibold bg-slate-800 text-white rounded-lg px-3.5 py-2 disabled:opacity-50">
                      {saving ? "Saving…" : "Save questions"}
                    </button>
                    <button onClick={() => { setEditing(false); setCodes(data.question_codes); }}
                      className="text-sm text-slate-600 px-3 py-2">Cancel</button>
                  </div>
                </div>
              )}

              <h3 className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-2">Students</h3>
              <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
                {data.attempts.map((a) => (
                  <div key={a.attempt_id} className="px-3 py-3 flex flex-wrap items-center gap-3">
                    <span className="font-medium text-sm flex-1 min-w-[110px]">{a.student_name}</span>
                    {a.status === "submitted"
                      ? <span className="text-xs text-green-700 inline-flex items-center gap-1"><CheckCircle2 size={13} /> handed in</span>
                      : <span className="text-xs text-slate-500">not handed in</span>}
                    <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <Camera size={13} /> {a.working_uploaded}
                    </span>
                    <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <ClipboardCheck size={13} /> {a.self_marked} marked
                    </span>
                    <button onClick={() => setReviewing(a.attempt_id)}
                      className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 hover:bg-slate-50 font-semibold">
                      <Eye size={12} /> Read &amp; mark
                    </button>
                    <a href={uploadUrl(a.upload_token)} target="_blank" rel="noreferrer"
                      className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-lg px-2 py-1 hover:bg-slate-50">
                      <QrCode size={12} /> Upload link <ExternalLink size={10} />
                    </a>
                  </div>
                ))}
                {data.attempts.length === 0 && <p className="px-3 py-6 text-sm text-slate-500">Nobody has this yet.</p>}
              </div>
              <p className="text-xs text-slate-400 mt-3">
                The upload link is what the student's QR code points at. No password, and it expires two weeks
                after the due date.
              </p>
            </>
          )}
        </div>
      </div>

      {reviewing && (
        <AttemptReview attemptId={reviewing} onClose={() => setReviewing(null)}
          onChanged={() => { load(); onChanged(); }} />
      )}
    </div>
  );
}

export default function AdminAssignments() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/assignments").then((r) => setRows(r.data || []))
      .catch((e) => toast.error(e.response?.data?.detail || "Could not load assignments"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <AdminShell title="Assignments" subtitle="Worksheets that have been given out">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        <p className="text-sm text-slate-500">
          {loading ? "Loading…" : `${rows.length} given out`}
        </p>
        <Link to="/admin/worksheets"
          className="inline-flex items-center gap-1.5 bg-slate-800 text-white text-sm font-semibold rounded-lg px-3.5 py-2">
          Give out a worksheet
        </Link>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        {loading && rows.length === 0 && (
          <p className="py-14 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
            <Loader2 size={15} className="animate-spin" /> Loading…
          </p>
        )}
        {!loading && rows.length === 0 && (
          <div className="py-14 text-center">
            <ClipboardList size={26} className="mx-auto text-slate-300 mb-3" />
            <p className="font-semibold text-slate-600">Nothing given out yet</p>
            <p className="text-sm text-slate-400 mt-1">
              Build a worksheet first, then give it to a group.
            </p>
          </div>
        )}
        {rows.map((a) => (
          <button key={a.id} onClick={() => setOpenId(a.id)}
            className="w-full text-left px-4 py-3.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span className="flex-1 min-w-[150px]">
              <span className="block font-medium text-slate-900">{a.title}</span>
              <span className="block text-xs text-slate-500 capitalize">
                {a.group_name ? `${a.group_name} · ` : ""}{a.kind} ·{" "}
                {a.question_count || a.question_codes?.length || 0} questions
                {a.due_date ? ` · due ${a.due_date}` : ""}
              </span>
            </span>
            <span className="text-xs text-slate-500 inline-flex items-center gap-1"><Users size={13} /> {a.assigned}</span>
            <span className={`text-xs inline-flex items-center gap-1 ${a.submitted > 0 ? "text-green-700" : "text-slate-400"}`}>
              <CheckCircle2 size={13} /> {a.submitted} handed in
            </span>
          </button>
        ))}
      </div>

      {openId && <Detail id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </AdminShell>
  );
}
